import { FC, useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { deepEqual } from 'fast-equals';
import { useSnackbar } from 'notistack';
// Components
import { Add, Info, School } from '@mui/icons-material';
import {
  FormHelperText,
  Fade,
  Typography,
  Button,
  Grid,
  TextField,
  Box,
  FormControl,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import { IMunicipalityDetail, IPermitValuationMethod, IMlsContract } from '../../../models';
import {
  createMunicipality,
  updateMunicipality,
  getMunicipalityPermitValuationMethods,
  getMunicipalityMlsContracts
} from '../../../fetch';
import {
  StatesDropdown,
  ModalSaveSection,
  CountiesDropdown,
  TerritoryDropdown,
  Modal,
  Loader,
  Card,
  SMBSelect
} from '../../../components';
import { formatMoney, decimalLengthFourRegExp } from '../../../helpers';
import { isMobile } from 'react-device-detect';
import { SchoolDistrict } from './SchoolDistricts';

interface IMunicipalityModal {
  open: boolean;
  onClose: () => void;
  currentMunicipality?: IMunicipalityDetail;
  fetchMunicipalities: () => {};
}

const MunicipalitySchema = Yup.object().shape({
  name: Yup.string().max(255, 'Max 255 characters').required('Required'),
  territoryId: Yup.string().max(255, 'Max 255 characters').required('Required'),
  salesTax: Yup.string().max(255, 'Max 255 characters'),
  millageRate: Yup.string().max(255, 'Max 255 characters'),
  permitValuationMethod: Yup.string().max(255, 'Max 255 characters').required('Required'),
  valuationPercent: Yup.string().max(255, 'Max 255 characters'),
  mlsContract: Yup.string().max(255, 'Max 255 characters').required('Required'),
  county: Yup.string().max(255, 'Max 255 characters').required('Required'),
  state: Yup.string().max(255, 'Max 255 characters').required('Required'),
  hasInspectionDept: Yup.string().required('Required'),
  isAssignAddressAtPlat: Yup.string(),
  trafficImpactFees: Yup.string().max(255, 'Max 255 characters'),
  schoolImpactFees: Yup.string().max(255, 'Max 255 characters'),
  otherFees: Yup.string().max(255, 'Max 255 characters'),
  schoolSystem: Yup.object().shape({
    districts: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required('Required'),
        schools: Yup.array()
          .of(
            Yup.object().shape({
              name: Yup.string().required('Required'),
              files: Yup.array()
                .of(
                  Yup.object().shape({
                    name: Yup.string(),
                    contents: Yup.string()
                  })
                )
                .required('Required')
            })
          )
          .required('Required')
      })
    ),
    name: Yup.string(),
    url: Yup.string(),
    narrative: Yup.string()
  })
});

export const MunicipalityModal: FC<IMunicipalityModal> = ({
  open,
  onClose,
  currentMunicipality,
  fetchMunicipalities
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedState, setSelectedState] = useState<string>('');
  const [methods, setMethods] = useState<IPermitValuationMethod[]>([]);
  const [mlsContracts, setMlsContracts] = useState<IMlsContract[]>([]);
  const [isLoadingMlsContracts, setLoadingMlsContracts] = useState<boolean>(true);
  const [isLoadingPermitValuationMethods, setIsLoadingPermitValuationMethods] = useState(true);
  const [actualTrafficFees, setActualTrafficFees] = useState<number>(0);
  const [actualSchoolFees, setActualSchoolFees] = useState<number>(0);
  const [actualOtherFees, setActualOtherFees] = useState<number>(0);
  const [actualSalesTax, setActualSalesTax] = useState<number>(0);
  const [actualValuationPercent, setActualValutionPercent] = useState<number>(0);

  const fetchPermitValuationMethods = async () => {
    try {
      setIsLoadingPermitValuationMethods(true);
      const res = await getMunicipalityPermitValuationMethods();
      setMethods(res);
    } catch (error) {
      enqueueSnackbar(`Error loading permit valuation methods, please try again.`, {
        variant: 'error'
      });
      console.log(error);
    } finally {
      setIsLoadingPermitValuationMethods(false);
    }
  };

  const fetchMlsContracts = async () => {
    try {
      setLoadingMlsContracts(true);
      const res = await getMunicipalityMlsContracts();
      setMlsContracts(res);
    } catch (error) {
      enqueueSnackbar(`Error loading mls contracts, please try again.`, {
        variant: 'error'
      });
      console.log(error);
    } finally {
      setLoadingMlsContracts(false);
    }
  };

  useEffect(() => {
    fetchPermitValuationMethods();
    fetchMlsContracts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentMunicipality) {
      setSelectedState(currentMunicipality.state);
      setActualOtherFees(currentMunicipality.otherFees);
      setActualSchoolFees(currentMunicipality.schoolImpactFees);
      setActualTrafficFees(currentMunicipality.trafficImpactFees);
      setActualSalesTax(currentMunicipality.salesTax);
      setActualValutionPercent(currentMunicipality.valuationPercent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMunicipality]);

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          name: currentMunicipality?.name ?? '',
          territoryId: currentMunicipality?.territoryId ?? '',
          salesTax: currentMunicipality?.salesTax ? `${currentMunicipality.salesTax}%` : '',
          millageRate: currentMunicipality?.millageRate ?? '',
          permitValuationMethod: currentMunicipality?.permitValuationMethod ?? '',
          valuationPercent: currentMunicipality?.valuationPercent
            ? `${currentMunicipality.valuationPercent}%`
            : '',
          state: currentMunicipality?.state ?? '',
          mlsContract: currentMunicipality?.mlsContract ?? '',
          county: currentMunicipality?.county ?? '',
          hasInspectionDept:
            currentMunicipality?.hasInspectionDept === true
              ? 'Yes'
              : currentMunicipality?.hasInspectionDept === false
              ? 'No'
              : '',
          isAssignAddressAtPlat:
            currentMunicipality?.isAssignAddressAtPlat === true
              ? 'Yes'
              : currentMunicipality?.isAssignAddressAtPlat === false
              ? 'No'
              : '',
          trafficImpactFees: currentMunicipality
            ? formatMoney(currentMunicipality.trafficImpactFees, 2)
            : '',
          otherFees: currentMunicipality ? formatMoney(currentMunicipality.otherFees, 2) : '',
          schoolImpactFees: currentMunicipality
            ? formatMoney(currentMunicipality.schoolImpactFees, 2)
            : '',
          schoolSystem: currentMunicipality?.schoolSystem ?? {
            districts: [
              {
                schools: [
                  {
                    files: [],
                    name: ''
                  }
                ],
                name: ''
              }
            ],
            name: '',
            url: '',
            narrative: ''
          }
        }}
        validationSchema={MunicipalitySchema}
        onSubmit={async (values, actions) => {
          try {
            const data = {
              ...values,
              territoryId: Number(values.territoryId),
              salesTax: actualSalesTax,
              millageRate: Number(values.millageRate),
              trafficImpactFees: actualTrafficFees,
              otherFees: actualOtherFees,
              schoolImpactFees: actualSchoolFees,
              valuationPercent: actualValuationPercent,
              hasInspectionDept: values.hasInspectionDept === 'Yes' ? true : false,
              isAssignAddressAtPlat: values.isAssignAddressAtPlat === 'Yes' ? true : false
            };
            if (currentMunicipality?.municipalityId) {
              await updateMunicipality(currentMunicipality.municipalityId, data);
            } else {
              await createMunicipality(data);
            }
            onClose();
            enqueueSnackbar(
              currentMunicipality ? 'Municipality Updated!' : 'Municipality Created!',
              {
                variant: 'success'
              }
            );
            fetchMunicipalities();
            setSelectedState('');
            actions.resetForm();
          } catch (error) {
            return enqueueSnackbar(error?.Detail ?? `Error saving municipality.`, {
              variant: 'error'
            });
          }
        }}
      >
        {({
          resetForm,
          isSubmitting,
          values,
          initialValues,
          setFieldValue,
          errors,
          touched,
          handleSubmit,
          dirty,
          isValid,
          handleBlur
        }) => {
          const handleClose = () => {
            if (!deepEqual(initialValues, values)) {
              const result = window.confirm(
                'You have unsaved changes, are you sure you want to exit?'
              );
              if (result) {
                resetForm();
                setSelectedState('');
                onClose();
              } else {
                return;
              }
            } else {
              setSelectedState('');
              onClose();
            }
          };
          return (
            <Modal
              open={open}
              onClose={handleClose}
              maxWidth="md"
              title={currentMunicipality ? `Edit Municipality` : 'Add Municipality'}
            >
              {isSubmitting && <Loader type="overlay" position="centered" />}
              <Fade in={open}>
                <Form onSubmit={handleSubmit} autoComplete="none">
                  <Box mt={1}>
                    <Card icon={<Info />} title="General Info">
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <TerritoryDropdown
                            isRequired
                            hasError={touched?.territoryId && errors?.territoryId ? true : false}
                            value={values.territoryId}
                            handleClear={() => setFieldValue('territoryId', '')}
                            message={
                              touched?.territoryId && errors?.territoryId ? 'Required' : undefined
                            }
                            handleChange={val => {
                              setFieldValue('territoryId', val);
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            required
                            variant="outlined"
                            autoComplete="nope"
                            label="Municipality Name"
                            name="name"
                            value={values.name}
                            onBlur={handleBlur}
                            size="small"
                            onChange={e => setFieldValue('name', e.target.value)}
                            error={touched.name && errors && errors.name ? true : false}
                            helperText={touched.name && errors && errors.name}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <StatesDropdown
                            isRequired
                            message={touched?.state && errors?.state ? 'Required' : undefined}
                            hasError={touched?.state && errors?.state ? true : undefined}
                            value={values.state}
                            handleChange={val => {
                              setFieldValue('state', val);
                              setFieldValue('county', '');
                              setSelectedState(`${val}`);
                            }}
                            isAdminPortal={true}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <CountiesDropdown
                            isRequired
                            isDisabled={!values.state}
                            message={touched?.county && errors?.county ? 'Required' : undefined}
                            hasError={touched?.county && errors?.county ? true : undefined}
                            value={values.county}
                            apiParams={{
                              sortBy: 'Name',
                              stateAbbreviation: selectedState
                            }}
                            handleChange={val => {
                              setFieldValue('county', val);
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            autoComplete="nope"
                            label="Sales Tax"
                            name="salesTax"
                            size="small"
                            value={values.salesTax}
                            onBlur={e => {
                              // Need to strip out all non numeric values from formatted value
                              const value = e.target.value.replace('%', '').replace(',', '');
                              // make sure it is number only before we update state
                              if (value === '' || decimalLengthFourRegExp.test(value)) {
                                setActualSalesTax(Number(value));
                              }
                              if (e.target.value) {
                                setFieldValue('salesTax', `${e.target.value}%`);
                              }
                            }}
                            onChange={e => {
                              // if value is not blank, then test the regex
                              if (
                                e.target.value === '' ||
                                decimalLengthFourRegExp.test(e.target.value)
                              ) {
                                setFieldValue('salesTax', e.target.value);
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            autoComplete="nope"
                            label="Millage Rate"
                            name="millageRate"
                            size="small"
                            value={values.millageRate}
                            onBlur={handleBlur}
                            error={Boolean(
                              touched.millageRate && +values.millageRate >= 1 ? true : false
                            )}
                            helperText={
                              touched.millageRate && +values.millageRate >= 1
                                ? 'Must be less than one'
                                : ''
                            }
                            onChange={e => {
                              // if value is not blank, then test the regex
                              if (
                                e.target.value === '' ||
                                decimalLengthFourRegExp.test(e.target.value)
                              ) {
                                setFieldValue('millageRate', e.target.value);
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl required fullWidth variant="outlined" size="small">
                            <SMBSelect
                              required
                              name="permitValuationMethod"
                              label="Permit Valuation Method"
                              variant="outlined"
                              id="permitValuationMethod"
                              handleClear={() => setFieldValue('permitValuationMethod', '')}
                              disabled={isLoadingPermitValuationMethods}
                              value={values.permitValuationMethod}
                              onChange={({ target: { value } }) => {
                                setFieldValue('permitValuationMethod', value);
                              }}
                            >
                              {methods &&
                                methods.map((method, index) => {
                                  return (
                                    <MenuItem key={`${index}`} value={method.value}>
                                      {method.description}
                                    </MenuItem>
                                  );
                                })}
                            </SMBSelect>
                            {touched &&
                              touched.permitValuationMethod &&
                              errors &&
                              errors.permitValuationMethod && (
                                <FormHelperText error>Required</FormHelperText>
                              )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            autoComplete="nope"
                            label="Valuation Percent"
                            name="valuationPercent"
                            value={values.valuationPercent}
                            onBlur={e => {
                              // Need to strip out all non numeric values from formatted value
                              const value = e.target.value.replace('%', '').replace(',', '');
                              // make sure it is number only before we update state
                              if (value === '' || decimalLengthFourRegExp.test(value)) {
                                setActualValutionPercent(Number(value));
                              }
                              if (e.target.value) {
                                setFieldValue('valuationPercent', `${e.target.value}%`);
                              }
                            }}
                            size="small"
                            onChange={e => {
                              // if value is not blank, then test the regex
                              if (
                                e.target.value === '' ||
                                decimalLengthFourRegExp.test(e.target.value)
                              ) {
                                setFieldValue('valuationPercent', e.target.value);
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl
                            required
                            fullWidth
                            variant="outlined"
                            size="small"
                            error={
                              touched && touched.mlsContract && errors && errors.mlsContract
                                ? true
                                : false
                            }
                          >
                            <SMBSelect
                              required
                              label="MLS"
                              variant="outlined"
                              handleClear={() => setFieldValue('mlsContract', '')}
                              name="mlsContract"
                              id="mlsContract"
                              disabled={isLoadingMlsContracts}
                              value={values.mlsContract}
                              onChange={({ target: { value } }) => {
                                setFieldValue('mlsContract', value);
                              }}
                            >
                              {mlsContracts &&
                                mlsContracts.map((contract, index) => {
                                  return (
                                    <MenuItem key={`${index}`} value={contract.value}>
                                      {contract.description}
                                    </MenuItem>
                                  );
                                })}
                            </SMBSelect>
                            {touched && touched.mlsContract && errors && errors.mlsContract && (
                              <FormHelperText error>Required</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                      </Grid>
                      <Box marginTop="1rem" marginBottom="1rem">
                        <Grid container spacing={1}>
                          <Grid item xs={12} sm={4}>
                            <Box padding="0.5rem">
                              <FormControl fullWidth variant="outlined" size="small" required>
                                <Typography>Is there an Inspection Dept*</Typography>
                                <RadioGroup
                                  value={values.hasInspectionDept}
                                  onChange={e => setFieldValue('hasInspectionDept', e.target.value)}
                                  aria-label="hasInspectionDept"
                                  name="hasInspectionDept"
                                >
                                  <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                  <FormControlLabel value="No" control={<Radio />} label="No" />
                                </RadioGroup>
                              </FormControl>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Box padding="0.5rem">
                              <FormControl fullWidth variant="outlined" size="small">
                                <Typography>Does Municipality Assign Address at Plat</Typography>
                                <RadioGroup
                                  value={values.isAssignAddressAtPlat}
                                  onChange={e =>
                                    setFieldValue('isAssignAddressAtPlat', e.target.value)
                                  }
                                  aria-label="isAssignAddressAtPlat"
                                  name="isAssignAddressAtPlat"
                                >
                                  <FormControlLabel
                                    value="Yes"
                                    control={<Radio />}
                                    label="Yes (Assigned at Plat)"
                                  />
                                  <FormControlLabel
                                    value="No"
                                    control={<Radio />}
                                    label="No (Assigned at Building Permit)"
                                  />
                                </RadioGroup>
                              </FormControl>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            autoComplete="nope"
                            label="Traffic Impact Fees"
                            name="trafficImpactFees"
                            value={values.trafficImpactFees}
                            size="small"
                            onBlur={e => {
                              // Need to strip out all non numeric values from formatted value
                              const value = e.target.value.replace('$', '').replace(',', '');
                              // make sure it is number only before we update state
                              if (value === '' || decimalLengthFourRegExp.test(value)) {
                                setActualTrafficFees(Number(value));
                              }
                              setFieldValue('trafficImpactFees', formatMoney(e.target.value, 2));
                            }}
                            onChange={e => {
                              setFieldValue('trafficImpactFees', e.target.value);
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            autoComplete="nope"
                            label="School Impact Fees"
                            name="schoolImpactFees"
                            value={values.schoolImpactFees}
                            size="small"
                            onBlur={e => {
                              // Need to strip out all non numeric values from formatted value
                              const value = e.target.value.replace('$', '').replace(',', '');
                              // make sure it is number only before we update state
                              if (value === '' || decimalLengthFourRegExp.test(value)) {
                                setActualSchoolFees(Number(value));
                              }
                              setFieldValue('schoolImpactFees', formatMoney(e.target.value, 2));
                            }}
                            onChange={e => {
                              setFieldValue('schoolImpactFees', e.target.value);
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            autoComplete="nope"
                            label="Other Fees"
                            name="otherFees"
                            value={values.otherFees}
                            size="small"
                            onBlur={e => {
                              // Need to strip out all non numeric values from formatted value
                              const value = e.target.value.replace('$', '').replace(',', '');
                              // make sure it is number only before we update state
                              if (value === '' || decimalLengthFourRegExp.test(value)) {
                                setActualOtherFees(Number(value));
                              }
                              setFieldValue('otherFees', formatMoney(e.target.value, 2));
                            }}
                            onChange={e => {
                              setFieldValue('otherFees', e.target.value);
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Card>
                    <Card icon={<School />} title="School Information">
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            autoComplete="nope"
                            label="School System Name"
                            name="values.schoolSystem.name"
                            value={values.schoolSystem.name}
                            onBlur={handleBlur}
                            size="small"
                            onChange={e => setFieldValue('schoolSystem.name', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            autoComplete="nope"
                            label="School System Url"
                            name="values.schoolSystem.url"
                            value={values.schoolSystem.url}
                            size="small"
                            onChange={e => setFieldValue('schoolSystem.url', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            variant="outlined"
                            autoComplete="nope"
                            label="School System Narrative"
                            name="schoolSystemNarrative"
                            value={values.schoolSystem.narrative}
                            onBlur={handleBlur}
                            size="small"
                            onChange={e => setFieldValue('schoolSystem.narrative', e.target.value)}
                          />
                        </Grid>

                        {values?.schoolSystem?.districts?.length > 0 && (
                          <Grid item xs={12}>
                            <Typography variant="h6">Districts</Typography>
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          <SchoolDistrict
                            isMobile={isMobile}
                            schoolDistricts={values.schoolSystem.districts}
                            setFieldValue={setFieldValue}
                            values={values}
                            handleBlur={handleBlur}
                            touched={touched}
                            errors={errors}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<Add />}
                            onClick={() => {
                              setFieldValue('schoolSystem.districts', [
                                ...(values.schoolSystem.districts || []),
                                {
                                  name: '',
                                  schools: [
                                    {
                                      name: '',
                                      files: []
                                    }
                                  ]
                                }
                              ]);
                            }}
                          >
                            Add District
                          </Button>
                        </Grid>
                      </Grid>
                    </Card>
                  </Box>

                  <ModalSaveSection
                    handleCancel={handleClose}
                    isSaveDisabled={!dirty || isSubmitting || !isValid}
                    handleSave={handleSubmit}
                  />
                </Form>
              </Fade>
            </Modal>
          );
        }}
      </Formik>
    </>
  );
};
